/** Props Interface */
interface RoleCapabilityCandidate {
	name: string;
	isSystem: boolean;
}

/**
 * Whether a role's permission grid is editable. False only for the `admin` system role — it is
 * `manage all` by definition, and the server's `patchRolePermissions` refuses it with 403
 * (`apps/server/src/handlers/roles/index.ts`). The name is part of the test on purpose: a
 * customer may create a role named "admin", and without the flag it is not this role.
 *
 * @param role - The role's name and system flag, as the list and detail endpoints return them.
 * @returns `true` when the permission editor may write the role.
 */
export function canEditPermissions({ name, isSystem }: RoleCapabilityCandidate): boolean {
	return !(isSystem && name === "admin");
}

/**
 * Whether a role's member set is editable. False only for the `member` system role: every
 * non-owner carries it by standing — assigned at joining, kept until leaving — and the server's
 * membership writes refuse it with 403.
 *
 * @param role - The role's name and system flag, as the list and detail endpoints return them.
 * @returns `true` when members may be added to and removed from the role.
 */
export function canManageMembers({ name, isSystem }: RoleCapabilityCandidate): boolean {
	return !(isSystem && name === "member");
}

/**
 * Whether the role's row menu has anything to offer. Rename, delete and duplicate are all
 * refused for every system role — mirroring the server's `not "isSystem"` guards — so `admin`
 * and `member` render a lock instead of a menu.
 *
 * @param role - The role's name and system flag, as the list and detail endpoints return them.
 * @returns `true` when rename, duplicate and delete are available.
 */
export function hasRoleMenu({ isSystem }: RoleCapabilityCandidate): boolean {
	return !isSystem;
}
